/**
 * CoreLogos — StudoX App-Emblems  ///  v2 "CELESTIAL GATES"
 * ─────────────────────────────────────────────────────────────────────────────
 * The CoreMark (Nexus Gateway) is the design north star: a dark glass gate,
 * a live portal ring with an orbiting energy sweep, volumetric orbs with real
 * rim-light, faceted crystals, lens sparks. Every app now gets an emblem in
 * EXACTLY that language — one family, 20 instruments of the Command Bridge.
 *
 * Shared anatomy (the Emblem base):
 *   · colour aura breathing behind the gate
 *   · dark glass disc (radial depth, not flat fill)
 *   · portal ring in the app's energy gradient + white core-line + HUD ticks
 *   · a white energy dash orbiting the ring (SMIL — alive everywhere)
 *   · the app's 3D glyph scene in the middle + glass top gloss
 *
 * API unchanged: <XxxMark size glow animated dimmed />
 */

/* ── shared emblem base ──────────────────────────────────────────────────── */
function Emblem({ id, size = 80, glow = false, dimmed = false, ca, cb, children }) {
  const u = `sxe-${id}`;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ overflow: "visible", opacity: dimmed ? 0.5 : 1 }}>
      <defs>
        <radialGradient id={`${u}-aura`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor={ca} stopOpacity="0.55" />
          <stop offset="55%" stopColor={ca} stopOpacity="0.16" />
          <stop offset="100%" stopColor={ca} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${u}-glass`} cx="38%" cy="28%" r="80%">
          <stop offset="0%"  stopColor="#2c1f52" />
          <stop offset="55%" stopColor="#150d31" />
          <stop offset="100%" stopColor="#080319" />
        </radialGradient>
        <linearGradient id={`${u}-ring`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor={ca} />
          <stop offset="55%"  stopColor={cb} />
          <stop offset="100%" stopColor={ca} />
        </linearGradient>
        <radialGradient id={`${u}-oa`} cx="35%" cy="27%" r="78%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="45%" stopColor={ca} />
          <stop offset="100%" stopColor="#10072a" />
        </radialGradient>
        <radialGradient id={`${u}-ob`} cx="35%" cy="27%" r="78%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="45%" stopColor={cb} />
          <stop offset="100%" stopColor="#10072a" />
        </radialGradient>
        {glow && (
          <filter id={`${u}-glow`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        )}
      </defs>

      {/* breathing colour aura */}
      <circle cx="40" cy="40" r="40" fill={`url(#${u}-aura)`}>
        <animate attributeName="opacity" values="0.8;1;0.8" dur="3.4s" repeatCount="indefinite" />
      </circle>

      <g filter={glow ? `url(#${u}-glow)` : undefined}>
        {/* dark glass gate */}
        <circle cx="40" cy="40" r="29.5" fill={`url(#${u}-glass)`} />
        <circle cx="40" cy="40" r="29.5" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="0.8" />

        {/* portal ring — energy gradient + white core-line + HUD ticks */}
        <circle cx="40" cy="40" r="33.4" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="2.7" opacity="0.95" />
        <circle cx="40" cy="40" r="33.4" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          return (
            <line key={i}
              x1={40 + 31.4 * Math.cos(a)} y1={40 + 31.4 * Math.sin(a)}
              x2={40 + 35.5 * Math.cos(a)} y2={40 + 35.5 * Math.sin(a)}
              stroke="rgba(255,255,255,0.20)" strokeWidth="0.6" />
          );
        })}

        {/* orbiting energy sweep — the emblem is powered, always */}
        <circle cx="40" cy="40" r="33.4" fill="none" stroke="#ffffff" strokeWidth="1.1" strokeLinecap="round"
          strokeDasharray="11 199" opacity="0.85">
          <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="6.5s" repeatCount="indefinite" />
        </circle>

        {/* the app's 3D glyph scene */}
        {children}

        {/* glass top gloss */}
        <ellipse cx="33" cy="25" rx="16" ry="8.5" fill="#ffffff" opacity="0.07" />
      </g>
    </svg>
  );
}

/* tiny lens spark — rotating glint cross */
function Spark({ x, y, s = 5, dur = "5s" }) {
  return (
    <g opacity="0.9">
      <animateTransform attributeName="transform" type="rotate" from={`0 ${x} ${y}`} to={`360 ${x} ${y}`} dur={dur} repeatCount="indefinite" />
      <rect x={x - s} y={y - 0.4} width={s * 2} height="0.8" fill="#fff" opacity="0.75" />
      <rect x={x - 0.4} y={y - s} width="0.8" height={s * 2} fill="#fff" opacity="0.75" />
    </g>
  );
}

/* ═══════════════════════════════ MOTOR ══════════════════════════════════ */

/* Flow — a crystalline graduation cap surfing a comet of momentum */
export function FlowMark({ size, glow, animated, dimmed }) {
  const u = "sxe-flow";
  return (
    <Emblem id="flow" size={size} glow={glow} dimmed={dimmed} ca="#8b5cf6" cb="#22d3ee">
      {/* comet swoosh under the cap */}
      <path d="M22 52 q14 -8 34 -3" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="2.6" strokeLinecap="round" opacity="0.85" />
      <circle cx="56" cy="49" r="3.1" fill={`url(#${u}-ob)`} />
      <circle cx="56" cy="49" r="3.1" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
      {/* faceted mortarboard */}
      <polygon points="40,22 59,31 40,40 21,31" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" strokeLinejoin="round" />
      <polygon points="40,22 59,31 40,40" fill="#ffffff" opacity="0.14" />
      <path d="M30 36 v7 q10 6 20 0 v-7" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2.6" strokeLinecap="round" />
      {/* tassel with glowing gold bead */}
      <path d="M59 31 v10" stroke="rgba(255,255,255,0.6)" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="59" cy="44" r="2.4" fill="#fde047">
        <animate attributeName="opacity" values="1;0.55;1" dur="1.8s" repeatCount="indefinite" />
      </circle>
      <Spark x={26} y={24} s={4} dur="6s" />
    </Emblem>
  );
}

/* Quest — a faceted compass star locked between green earth & golden reward */
export function QuestMark({ size, glow, animated, dimmed }) {
  const u = "sxe-quest";
  return (
    <Emblem id="quest" size={size} glow={glow} dimmed={dimmed} ca="#22c55e" cb="#fbbf24">
      <circle cx="40" cy="40" r="19.5" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.7" opacity="0.8" />
      <circle cx="40" cy="40" r="19.5" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" strokeDasharray="1.5 4.8" />
      {/* N-E-S-W micro markers */}
      {[0, 90, 180, 270].map((a) => (
        <circle key={a} cx={40 + 19.5 * Math.cos((a * Math.PI) / 180)} cy={40 + 19.5 * Math.sin((a * Math.PI) / 180)} r="1.3" fill="rgba(255,255,255,0.7)" />
      ))}
      {/* faceted needle */}
      <g>
        <animateTransform attributeName="transform" type="rotate" values="-10 40 40; 12 40 40; -10 40 40" dur="4s" repeatCount="indefinite" />
        <polygon points="40,24 46,40 40,44.5 34,40" fill={`url(#${u}-ob)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" strokeLinejoin="round" />
        <polygon points="40,24 46,40 40,44.5" fill="#ffffff" opacity="0.16" />
        <polygon points="40,56 34,40 40,35.5 46,40" fill={`url(#${u}-oa)`} opacity="0.9" strokeLinejoin="round" />
      </g>
      <circle cx="40" cy="40" r="4" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />
      <circle cx="40" cy="40" r="1.4" fill="#fff" />
    </Emblem>
  );
}

/* Step — three glass steps ascending to a burning gold star */
export function StepMark({ size, glow, animated, dimmed }) {
  const u = "sxe-step";
  return (
    <Emblem id="step" size={size} glow={glow} dimmed={dimmed} ca="#f97316" cb="#fbbf24">
      {/* 3D steps: front face + lighter top face */}
      {[{ x: 22, y: 50, w: 12 }, { x: 32, y: 42, w: 12 }, { x: 42, y: 34, w: 12 }].map((s2, i) => (
        <g key={i}>
          <rect x={s2.x} y={s2.y} width={s2.w} height={58 - s2.y} rx="1.6" fill={`url(#${u}-oa)`} opacity={0.75 + i * 0.12} />
          <polygon points={`${s2.x},${s2.y} ${s2.x + s2.w},${s2.y} ${s2.x + s2.w + 3},${s2.y - 3} ${s2.x + 3},${s2.y - 3}`} fill="#ffffff" opacity="0.35" />
          <rect x={s2.x} y={s2.y} width={s2.w} height={58 - s2.y} rx="1.6" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="0.6" />
        </g>
      ))}
      {/* the goal star — gold orb + spark */}
      <circle cx="57" cy="24" r="4.6" fill={`url(#${u}-ob)`}>
        <animate attributeName="r" values="4.6;5.3;4.6" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="57" cy="24" r="4.6" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.6" />
      <Spark x={57} y={24} s={7} dur="7s" />
    </Emblem>
  );
}

/* ═══════════════════════════════ HIRN ═══════════════════════════════════ */

/* Adulting — der Kristall-Schlüssel zum echten Leben: volumetrischer Bow,
   Energie-Schaft mit Level-Pips, Bart schließt oben rechts das
   Erwachsenen-Level auf (goldener Unlock-Funke) */
export function AdultingMark({ size, glow, animated, dimmed }) {
  const u = "sxe-adulting";
  return (
    <Emblem id="adulting" size={size} glow={glow} dimmed={dimmed} ca="#3ddc97" cb="#4cc9f0">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0 0; 0 -1.8; 0 0" dur="3.4s" repeatCount="indefinite" />
        {/* Unter-Glow des Schlüssels */}
        <path d="M34.7 45.3 L57 23" stroke={`url(#${u}-ring)`} strokeWidth="9.5" strokeLinecap="round" opacity="0.18" />
        {/* Schaft mit weißer Core-Line */}
        <path d="M34.7 45.3 L57 23" stroke={`url(#${u}-ring)`} strokeWidth="5" strokeLinecap="round" />
        <path d="M34.7 45.3 L57 23" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" opacity="0.55" />
        {/* Bart — zwei Zähne */}
        <path d="M52.8 27.2 l4.6 4.6" stroke={`url(#${u}-ring)`} strokeWidth="4.4" strokeLinecap="round" />
        <path d="M57 23 l4.6 4.6" stroke={`url(#${u}-ring)`} strokeWidth="4.4" strokeLinecap="round" />
        <path d="M57 23 l4.6 4.6" stroke="#ffffff" strokeWidth="0.9" strokeLinecap="round" opacity="0.4" />
        {/* Level-Pips wandern den Schaft hinauf */}
        {[[40.3, 39.7], [45.9, 34.1], [51.5, 28.5]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.35" fill="#fff">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.9s" begin={`${i * 0.32}s`} repeatCount="indefinite" />
          </circle>
        ))}
        {/* Energie-Puls läuft den Schaft entlang */}
        <path d="M34.7 45.3 L57 23" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="4 60" opacity="0.9">
          <animate attributeName="stroke-dashoffset" values="64;0" dur="2.2s" repeatCount="indefinite" />
        </path>
        {/* Bow — volumetrischer Kristallring mit Rim-Light */}
        <circle cx="29" cy="51" r="9.2" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.45)" strokeWidth="0.8" />
        <circle cx="29" cy="51" r="9.2" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.4" opacity="0.8" />
        <circle cx="29" cy="51" r="3.6" fill="#0b0522" stroke="rgba(255,255,255,0.5)" strokeWidth="0.7" />
        <ellipse cx="25.6" cy="46.8" rx="4.6" ry="2.6" fill="#ffffff" opacity="0.3" transform="rotate(-32 25.6 46.8)" />
        {/* goldener Unlock-Funke am Bart */}
        <circle cx="61.4" cy="27.4" r="2.5" fill="#fde047">
          <animate attributeName="opacity" values="1;0.5;1" dur="1.7s" repeatCount="indefinite" />
        </circle>
      </g>
      <Spark x={64} y={15} s={5.5} dur="6s" />
    </Emblem>
  );
}

/* Cinema — a light-beam projector igniting a crystal play-glyph */
export function CinemaMark({ size, glow, animated, dimmed }) {
  const u = "sxe-cinema";
  return (
    <Emblem id="cinema" size={size} glow={glow} dimmed={dimmed} ca="#f59e0b" cb="#fb923c">
      {/* projector light cone */}
      <polygon points="26,40 62,26 62,54" fill={`url(#${u}-ring)`} opacity="0.16" />
      {/* film sprocket dots arcing above & below */}
      {[-1, 1].map((s2) =>
        [0, 1, 2, 3].map((i) => (
          <circle key={`${s2}-${i}`} cx={26 + i * 9.5} cy={40 + s2 * 20} r="1.5" fill="rgba(255,255,255,0.5)">
            <animate attributeName="opacity" values={i % 2 ? "0.8;0.3;0.8" : "0.3;0.8;0.3"} dur="1.6s" repeatCount="indefinite" />
          </circle>
        ))
      )}
      {/* faceted play crystal */}
      <polygon points="32,27 56,40 32,53" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" strokeLinejoin="round" />
      <polygon points="32,27 56,40 32,40" fill="#ffffff" opacity="0.18" />
      <polygon points="32,27 32,53 36.5,50.5 36.5,29.5" fill="#ffffff" opacity="0.10" />
      <Spark x={54} y={28} s={4.5} dur="5.5s" />
    </Emblem>
  );
}

/* ═══════════════════════════ WERKSTATT ══════════════════════════════════ */

/* Create — a crystal pen nib dragging a rainbow of light */
export function CreateMark({ size, glow, animated, dimmed }) {
  const u = "sxe-create";
  return (
    <Emblem id="create" size={size} glow={glow} dimmed={dimmed} ca="#f43f5e" cb="#fb923c">
      {/* rainbow stroke being drawn */}
      <path d="M22 56 q12 4 34 -2" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="3" strokeLinecap="round" opacity="0.9">
        <animate attributeName="opacity" values="0.9;0.55;0.9" dur="2.2s" repeatCount="indefinite" />
      </path>
      {/* faceted nib */}
      <path d="M31 50 L26 58 L34 54 L55 30 a5 5 0 0 0 -8.2 -6.6 Z" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.7" strokeLinejoin="round" />
      <path d="M46.8 23.4 L55 30 L40 47 Z" fill="#ffffff" opacity="0.15" />
      <path d="M37 43 q6 -8 13 -15" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" strokeLinecap="round" />
      <circle cx="30" cy="54.5" r="1.6" fill="#0f0524" opacity="0.65" />
      {/* inspiration star */}
      <path d="M58 18 l1.7 4.2 4.2 1.7 -4.2 1.7 -1.7 4.2 -1.7 -4.2 -4.2 -1.7 4.2 -1.7 Z" fill="#fde047">
        <animate attributeName="opacity" values="1;0.45;1" dur="1.5s" repeatCount="indefinite" />
      </path>
    </Emblem>
  );
}

/* ═══════════════════════════════ HERZ ═══════════════════════════════════ */

/* Meet — two glass comm-orbs exchanging a live signal */
export function MeetMark({ size, glow, animated, dimmed }) {
  const u = "sxe-meet";
  return (
    <Emblem id="meet" size={size} glow={glow} dimmed={dimmed} ca="#7c3aed" cb="#22d3ee">
      {/* signal beam between the orbs */}
      <path d="M33 36 Q40 30 47 42" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.6" strokeDasharray="3 3" opacity="0.85">
        <animate attributeName="stroke-dashoffset" from="12" to="0" dur="0.9s" repeatCount="indefinite" />
      </path>
      {/* violet speech orb with video glyph */}
      <circle cx="30" cy="36" r="10.5" fill={`url(#${u}-oa)`} />
      <circle cx="30" cy="36" r="10.5" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
      <path d="M24 33.5 h7 v5 h-7 Z M31.5 35 l4 -2.3 v6.6 l-4 -2.3" fill="#ffffff" opacity="0.95" />
      <path d="M25 45 l-2.5 5 6 -3" fill={`url(#${u}-oa)`} />
      {/* cyan reply orb with voice bars */}
      <circle cx="52" cy="44" r="8.5" fill={`url(#${u}-ob)`} />
      <circle cx="52" cy="44" r="8.5" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
      {[{ x: 48.5, h: 4 }, { x: 51.5, h: 7 }, { x: 54.5, h: 5 }].map((b, i) => (
        <rect key={i} x={b.x} y={44 - b.h / 2} width="1.8" height={b.h} rx="0.9" fill="#fff">
          <animate attributeName="height" values={`${b.h};${b.h + 3};${b.h}`} dur={`${0.7 + i * 0.2}s`} repeatCount="indefinite" />
          <animate attributeName="y" values={`${44 - b.h / 2};${44 - (b.h + 3) / 2};${44 - b.h / 2}`} dur={`${0.7 + i * 0.2}s`} repeatCount="indefinite" />
        </rect>
      ))}
      <Spark x={26} y={27} s={3.5} dur="6s" />
    </Emblem>
  );
}

/* Clubs — three luminous member-orbs bound in one orbit */
export function ClubsMark({ size, glow, animated, dimmed }) {
  const u = "sxe-clubs";
  return (
    <Emblem id="clubs" size={size} glow={glow} dimmed={dimmed} ca="#10b981" cb="#2dd4bf">
      {/* binding orbit */}
      <ellipse cx="40" cy="43" rx="19" ry="10" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.6" opacity="0.8" />
      <ellipse cx="40" cy="43" rx="19" ry="10" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.4" />
      {/* connection lines to centre */}
      <path d="M40 30 V40 M26 48 L36 43 M54 48 L44 43" stroke="rgba(255,255,255,0.30)" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 2.4" />
      {/* member orbs (lead + two) */}
      <circle cx="40" cy="27" r="7.5" fill={`url(#${u}-oa)`} />
      <circle cx="40" cy="27" r="7.5" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.6" />
      <circle cx="25" cy="49" r="6" fill={`url(#${u}-ob)`} />
      <circle cx="25" cy="49" r="6" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
      <circle cx="55" cy="49" r="6" fill={`url(#${u}-ob)`} />
      <circle cx="55" cy="49" r="6" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
      {/* the shared core they orbit */}
      <circle cx="40" cy="43" r="2.6" fill="#fff">
        <animate attributeName="opacity" values="1;0.5;1" dur="1.8s" repeatCount="indefinite" />
      </circle>
    </Emblem>
  );
}

/* Events — a glass chrono-plate with a burning date-star */
export function EventsMark({ size, glow, animated, dimmed }) {
  const u = "sxe-events";
  return (
    <Emblem id="events" size={size} glow={glow} dimmed={dimmed} ca="#fbbf24" cb="#f97316">
      {/* glass calendar plate */}
      <rect x="23" y="27" width="34" height="28" rx="5" fill={`url(#${u}-glass)`} stroke={`url(#${u}-ring)`} strokeWidth="1.8" />
      <path d="M23 35 H57" stroke={`url(#${u}-ring)`} strokeWidth="2.2" />
      <path d="M31 22.5 V30 M49 22.5 V30" stroke="rgba(255,255,255,0.75)" strokeWidth="2.4" strokeLinecap="round" />
      {/* faint date grid */}
      {[40, 46].map((y) => [29, 36, 43, 50].map((x) => (
        <circle key={`${x}${y}`} cx={x} cy={y} r="0.9" fill="rgba(255,255,255,0.25)" />
      )))}
      {/* THE date — gold star orb */}
      <path d="M46 42 l2.1 4.2 4.6 0.7 -3.3 3.3 0.8 4.6 -4.2 -2.2 -4.2 2.2 0.8 -4.6 -3.3 -3.3 4.6 -0.7 Z" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" strokeLinejoin="round">
        <animate attributeName="opacity" values="1;0.7;1" dur="1.7s" repeatCount="indefinite" />
      </path>
    </Emblem>
  );
}

/* Discord — the community gamepad, carved from indigo glass */
export function DiscordMark({ size, glow, animated, dimmed }) {
  const u = "sxe-discord";
  return (
    <Emblem id="discord" size={size} glow={glow} dimmed={dimmed} ca="#818cf8" cb="#4338ca">
      <path d="M27 31 h26 q8 1 9 14.5 q0.5 8.5 -4.5 9 q-3.8 0.3 -6.5 -5 q-1.4 -2.4 -3.8 -2.4 h-14.4 q-2.4 0 -3.8 2.4 q-2.7 5.3 -6.5 5 q-5 -0.5 -4.5 -9 q1 -13.5 9 -14.5 Z"
        fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" strokeLinejoin="round" />
      <path d="M27 31 h26 q4 0.5 6.5 5 h-39 q2.5 -4.5 6.5 -5 Z" fill="#ffffff" opacity="0.13" />
      <path d="M31.5 37.5 v7 M28 41 h7" stroke="#ffffff" strokeWidth="2.6" strokeLinecap="round" />
      <circle cx="50" cy="38.5" r="2.2" fill="#fff">
        <animate attributeName="opacity" values="1;0.5;1" dur="1.3s" repeatCount="indefinite" />
      </circle>
      <circle cx="55" cy="43.5" r="2.2" fill="#fff">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="1.3s" repeatCount="indefinite" />
      </circle>
      <Spark x={56} y={28} s={4} dur="6s" />
    </Emblem>
  );
}

/* Invite — a light-letter breaking open with a golden plus-star */
export function InviteMark({ size, glow, animated, dimmed }) {
  const u = "sxe-invite";
  return (
    <Emblem id="invite" size={size} glow={glow} dimmed={dimmed} ca="#c4b5fd" cb="#7c3aed">
      <rect x="21" y="31" width="32" height="23" rx="4" fill={`url(#${u}-glass)`} stroke={`url(#${u}-ring)`} strokeWidth="1.8" />
      <path d="M23 34 L37 44.5 L51 34" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M23 51.5 L33 43.5 M51 51.5 L41 43.5" stroke="rgba(255,255,255,0.35)" strokeWidth="1.1" strokeLinecap="round" />
      {/* golden plus orb */}
      <circle cx="56" cy="52" r="8.6" fill={`url(#${u}-ob)`} />
      <circle cx="56" cy="52" r="8.6" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.7" />
      <path d="M56 48 V56 M52 52 H60" stroke="#ffffff" strokeWidth="2.6" strokeLinecap="round" />
      <Spark x={59} y={30} s={4} dur="5s" />
    </Emblem>
  );
}

/* ═══════════════════════════════ SEELE ══════════════════════════════════ */

/* Music — a comet-note over a living light equalizer */
export function MusicMark({ size, glow, animated, dimmed }) {
  const u = "sxe-music";
  return (
    <Emblem id="music" size={size} glow={glow} dimmed={dimmed} ca="#7c3aed" cb="#22d3ee">
      {/* light equalizer */}
      {[{ x: 22, h: 9, d: "1.0s" }, { x: 28, h: 15, d: "0.75s" }, { x: 34, h: 7, d: "1.3s" }].map((b) => (
        <rect key={b.x} x={b.x} y={58 - b.h} width="3.4" height={b.h} rx="1.7" fill={`url(#${u}-ring)`} opacity="0.85">
          <animate attributeName="height" values={`${b.h};${b.h + 9};${b.h}`} dur={b.d} repeatCount="indefinite" />
          <animate attributeName="y" values={`${58 - b.h};${49 - b.h};${58 - b.h}`} dur={b.d} repeatCount="indefinite" />
        </rect>
      ))}
      {/* the note — beam + two glass orb heads */}
      <path d="M47 50 V25 l13 -4.2 V46" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="43" cy="50" r="5.4" fill={`url(#${u}-oa)`} />
      <circle cx="43" cy="50" r="5.4" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.6" />
      <circle cx="56" cy="46" r="5.4" fill={`url(#${u}-ob)`} />
      <circle cx="56" cy="46" r="5.4" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.6" />
      <Spark x={59} y={23} s={4} dur="5.5s" />
    </Emblem>
  );
}

/* Phonk — a spinning vinyl singularity split by golden lightning */
export function PhonkMark({ size, glow, animated, dimmed }) {
  const u = "sxe-phonk";
  return (
    <Emblem id="phonk" size={size} glow={glow} dimmed={dimmed} ca="#a78bfa" cb="#ec4899">
      <g>
        <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="6s" repeatCount="indefinite" />
        <circle cx="40" cy="40" r="19.5" fill="#0b0620" />
        <circle cx="40" cy="40" r="19.5" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.8" />
        <circle cx="40" cy="40" r="14" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" strokeDasharray="9 6" />
        <circle cx="40" cy="40" r="9.5" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.7" strokeDasharray="5 5" />
        <ellipse cx="33" cy="32" rx="8" ry="4" fill="#ffffff" opacity="0.08" transform="rotate(-30 33 32)" />
      </g>
      <circle cx="40" cy="40" r="5" fill={`url(#${u}-oa)`} />
      <circle cx="40" cy="40" r="5" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />
      {/* golden strike */}
      <polygon points="46,15 34,40 42,40 33,64 53,35 44,35 54,15" fill="#fde047" stroke="rgba(120,53,15,0.55)" strokeWidth="0.8" strokeLinejoin="round">
        <animate attributeName="opacity" values="1;0.75;1;1;0.85;1" dur="2.6s" repeatCount="indefinite" />
      </polygon>
    </Emblem>
  );
}

/* Lyrics — a light-quill writing verses that glow */
export function LyricsMark({ size, glow, animated, dimmed }) {
  const u = "sxe-lyrics";
  return (
    <Emblem id="lyrics" size={size} glow={glow} dimmed={dimmed} ca="#ec4899" cb="#8b5cf6">
      {/* glowing verse lines */}
      {[{ y: 42, w: 15 }, { y: 49, w: 11 }, { y: 56, w: 17 }].map((l, i) => (
        <path key={l.y} d={`M22 ${l.y} h${l.w}`} stroke={`url(#${u}-ring)`} strokeWidth="2.2" strokeLinecap="round" opacity="0.85">
          <animate attributeName="opacity" values={i === 2 ? "0.4;0.95;0.4" : "0.85;0.5;0.85"} dur="2.4s" repeatCount="indefinite" />
        </path>
      ))}
      {/* crystal feather */}
      <path d="M60 17 q-17 2.5 -23 13 q-4 7.5 -1.8 16.5 q7.3 1 13.3 -4.5 q9.5 -8.5 11.5 -25 Z"
        fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.7" />
      <path d="M60 17 q-13 9 -22 29" stroke="rgba(255,255,255,0.55)" strokeWidth="1" fill="none" strokeLinecap="round" />
      <path d="M40 40 q6 -3 10 -8 M37 46 q7 -2.5 12 -7" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" strokeLinecap="round" />
      <path d="M35 47.5 l-4.5 11" stroke="rgba(255,255,255,0.85)" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="30" cy="59.5" r="1.7" fill="#fde047">
        <animate attributeName="opacity" values="1;0.4;1" dur="1.4s" repeatCount="indefinite" />
      </circle>
    </Emblem>
  );
}

/* Mic — a condenser capsule broadcasting rings of sound */
export function MicMark({ size, glow, animated, dimmed }) {
  const u = "sxe-mic";
  return (
    <Emblem id="mic" size={size} glow={glow} dimmed={dimmed} ca="#f9a8d4" cb="#be185d">
      {/* sound rings */}
      <path d="M21 30 a23 23 0 0 1 0 20" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.6" strokeLinecap="round" opacity="0.7">
        <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.7s" repeatCount="indefinite" />
      </path>
      <path d="M59 30 a23 23 0 0 0 0 20" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.6" strokeLinecap="round" opacity="0.7">
        <animate attributeName="opacity" values="0.2;0.8;0.2" dur="1.7s" repeatCount="indefinite" />
      </path>
      {/* glass capsule */}
      <rect x="33.5" y="18" width="13" height="24" rx="6.5" fill={`url(#${u}-oa)`} />
      <rect x="33.5" y="18" width="13" height="24" rx="6.5" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.7" />
      <path d="M33.5 26.5 h13 M33.5 32 h13" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
      <ellipse cx="37.5" cy="22.5" rx="2.6" ry="1.6" fill="#ffffff" opacity="0.5" transform="rotate(-18 37.5 22.5)" />
      {/* cradle + stand */}
      <path d="M27 34 v4 a13 13 0 0 0 26 0 v-4" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M40 51.5 V59 M32 59 H48" stroke="rgba(255,255,255,0.85)" strokeWidth="2.6" strokeLinecap="round" />
      {/* on-air dot */}
      <circle cx="40" cy="63.5" r="1.9" fill="#f43f5e">
        <animate attributeName="opacity" values="1;0.35;1" dur="1.1s" repeatCount="indefinite" />
      </circle>
    </Emblem>
  );
}

/* ═══════════════════════════ NERVENSYSTEM ═══════════════════════════════ */

/* Hub — the home planet with its golden satellite (CoreMark's little sibling) */
export function HubMark({ size, glow, animated, dimmed }) {
  const u = "sxe-hub";
  return (
    <Emblem id="hub" size={size} glow={glow} dimmed={dimmed} ca="#7c3aed" cb="#22d3ee">
      <ellipse cx="40" cy="40" rx="22" ry="9" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.7" opacity="0.85" transform="rotate(-24 40 40)" />
      <circle cx="40" cy="40" r="11" fill={`url(#${u}-oa)`} />
      <circle cx="40" cy="40" r="11" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.7" />
      <path d="M30.5 36 q9.5 -4 19 0 M30.5 44 q9.5 4 19 0" stroke="rgba(255,255,255,0.35)" strokeWidth="0.9" fill="none" strokeLinecap="round" />
      <ellipse cx="36" cy="34.5" rx="4.4" ry="2.6" fill="#ffffff" opacity="0.4" transform="rotate(-18 36 34.5)" />
      {/* golden satellite riding the orbit */}
      <g>
        <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="7s" repeatCount="indefinite" />
        <circle cx="60" cy="31" r="3.4" fill={`url(#${u}-ob)`} />
        <circle cx="60" cy="31" r="3.4" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" />
      </g>
      <Spark x={26} y={52} s={3.5} dur="6.5s" />
    </Emblem>
  );
}

/* Info — a beacon "i" carved in light */
export function InfoMark({ size, glow, animated, dimmed }) {
  const u = "sxe-info";
  return (
    <Emblem id="info" size={size} glow={glow} dimmed={dimmed} ca="#a78bfa" cb="#6d28d9">
      <circle cx="40" cy="40" r="18.5" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.7" opacity="0.85" />
      <circle cx="40" cy="40" r="18.5" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" strokeDasharray="1.5 5" />
      <circle cx="40" cy="30" r="4" fill={`url(#${u}-oa)`} />
      <circle cx="40" cy="30" r="4" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />
      <path d="M40 38.5 V50.5" stroke="rgba(255,255,255,0.92)" strokeWidth="4.6" strokeLinecap="round" />
      <path d="M35.5 51 H44.5" stroke="rgba(255,255,255,0.6)" strokeWidth="2.4" strokeLinecap="round" />
      <Spark x={52} y={26} s={4} dur="6s" />
    </Emblem>
  );
}

/* Impact — a crystal heart with the world inside */
export function ImpactMark({ size, glow, animated, dimmed }) {
  const u = "sxe-impact";
  return (
    <Emblem id="impact" size={size} glow={glow} dimmed={dimmed} ca="#fb7185" cb="#e11d48">
      <path d="M40 58 C22 46 18 34 25.5 27 c5.6 -5.6 12 -2.8 14.5 1.8 c2.5 -4.6 8.9 -7.4 14.5 -1.8 c7.5 7 3.5 19 -14.5 31 Z"
        fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" strokeLinejoin="round">
        <animate attributeName="opacity" values="1;0.85;1" dur="1.4s" repeatCount="indefinite" />
      </path>
      {/* globe meridians inside the heart */}
      <path d="M29 36 q11 -4.5 22 0 M29.5 43 q10.5 4.5 21 0 M40 27.5 v26" stroke="rgba(255,255,255,0.45)" strokeWidth="1" fill="none" strokeLinecap="round" />
      <ellipse cx="40" cy="40" rx="7" ry="12.5" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" />
      <path d="M31 30.5 q3 -3.5 6 -1.5" stroke="#ffffff" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.6" />
      <Spark x={54} y={24} s={4} dur="5s" />
    </Emblem>
  );
}

/* ═══════════════════════════ IMMUNSYSTEM ════════════════════════════════ */

/* Hilfe — a luminous lifebuoy, thrown the moment you need it */
export function HelpMark({ size, glow, animated, dimmed }) {
  const u = "sxe-help";
  return (
    <Emblem id="help" size={size} glow={glow} dimmed={dimmed} ca="#fb7185" cb="#be123c">
      <circle cx="40" cy="40" r="16.5" fill="none" stroke={`url(#${u}-oa)`} strokeWidth="8.5" />
      <circle cx="40" cy="40" r="21" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" />
      <circle cx="40" cy="40" r="12" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" />
      {[45, 135, 225, 315].map((a) => (
        <line key={a}
          x1={40 + 12 * Math.cos((a * Math.PI) / 180)} y1={40 + 12 * Math.sin((a * Math.PI) / 180)}
          x2={40 + 21 * Math.cos((a * Math.PI) / 180)} y2={40 + 21 * Math.sin((a * Math.PI) / 180)}
          stroke="rgba(11,4,26,0.6)" strokeWidth="2.6" />
      ))}
      {/* safe centre light */}
      <circle cx="40" cy="40" r="4.4" fill={`url(#${u}-ob)`}>
        <animate attributeName="r" values="4.4;5.2;4.4" dur="1.9s" repeatCount="indefinite" />
      </circle>
      <Spark x={55} y={25} s={4} dur="5.5s" />
    </Emblem>
  );
}

/* Support — the always-on headset, mic glowing ready */
export function SupportMark({ size, glow, animated, dimmed }) {
  const u = "sxe-support";
  return (
    <Emblem id="support" size={size} glow={glow} dimmed={dimmed} ca="#67e8f9" cb="#0e7490">
      <path d="M24 45 v-4.5 a16 16 0 0 1 32 0 V45" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="3" strokeLinecap="round" />
      <path d="M24 45 v-4.5 a16 16 0 0 1 32 0 V45" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" strokeLinecap="round" />
      <rect x="20.5" y="42" width="8.5" height="13" rx="4" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
      <rect x="51" y="42" width="8.5" height="13" rx="4" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
      <path d="M55 55 q0 6.5 -11 6.5" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="41.5" cy="61.5" r="2.6" fill={`url(#${u}-ob)`}>
        <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <Spark x={26} y={26} s={3.5} dur="6s" />
    </Emblem>
  );
}

/* Status — the ecosystem's heartbeat, drawn live */
export function StatusMark({ size, glow, animated, dimmed }) {
  const u = "sxe-status";
  return (
    <Emblem id="status" size={size} glow={glow} dimmed={dimmed} ca="#4ade80" cb="#16a34a">
      <path d="M20 40 h9 l4.5 -11 7 21 5.5 -13 2.6 3 h11.4" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <animate attributeName="stroke-dasharray" values="0 120; 120 0" dur="2.4s" repeatCount="indefinite" />
      </path>
      <path d="M20 40 h9 l4.5 -11 7 21 5.5 -13 2.6 3 h11.4" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="57" cy="40" r="3.4" fill={`url(#${u}-oa)`}>
        <animate attributeName="r" values="3.4;4.8;3.4" dur="1.1s" repeatCount="indefinite" />
      </circle>
      <circle cx="57" cy="40" r="6.5" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6">
        <animate attributeName="r" values="5;9;5" dur="1.1s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.7;0;0.7" dur="1.1s" repeatCount="indefinite" />
      </circle>
    </Emblem>
  );
}

/* ═══════════════════════════ FUNDAMENT ══════════════════════════════════ */

/* Invest — crystal growth towers under a comet trend-line */
export function InvestMark({ size, glow, animated, dimmed }) {
  const u = "sxe-invest";
  return (
    <Emblem id="invest" size={size} glow={glow} dimmed={dimmed} ca="#34d399" cb="#fbbf24">
      {/* three crystal towers with lit top faces */}
      {[{ x: 24, y: 46 }, { x: 35, y: 39 }, { x: 46, y: 31 }].map((b, i) => (
        <g key={i}>
          <rect x={b.x} y={b.y} width="8" height={58 - b.y} rx="1.4" fill={`url(#${u}-oa)`} opacity={0.7 + i * 0.15} />
          <polygon points={`${b.x},${b.y} ${b.x + 8},${b.y} ${b.x + 10.5},${b.y - 2.6} ${b.x + 2.5},${b.y - 2.6}`} fill="#ffffff" opacity="0.4" />
          <rect x={b.x} y={b.y} width="8" height={58 - b.y} rx="1.4" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
        </g>
      ))}
      {/* comet trend-line */}
      <path d="M22 38 L37 30 L46 33 L58 21" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="58" cy="21" r="3" fill={`url(#${u}-ob)`}>
        <animate attributeName="opacity" values="1;0.6;1" dur="1.6s" repeatCount="indefinite" />
      </circle>
      <Spark x={58} y={21} s={5.5} dur="5s" />
    </Emblem>
  );
}

/* ═══════════════════════════ GEFAHRENZONE ═══════════════════════════════ */

/* Looksmaxing — a mirror whose reflection is a heart: you are enough */
export function LooksMark({ size, glow, animated, dimmed }) {
  const u = "sxe-looks";
  return (
    <Emblem id="looks" size={size} glow={glow} dimmed={dimmed} ca="#22d3ee" cb="#a78bfa">
      {/* hand mirror frame */}
      <ellipse cx="40" cy="35" rx="15" ry="17" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="2.6" />
      <ellipse cx="40" cy="35" rx="15" ry="17" fill={`url(#${u}-glass)`} opacity="0.5" />
      <path d="M40 52 v10" stroke="rgba(255,255,255,0.55)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M34 62 h12" stroke="rgba(255,255,255,0.55)" strokeWidth="2.6" strokeLinecap="round" />
      {/* the reflection is a glowing heart */}
      <path d="M40 43 C33 37 32 31 36 28 c2.4 -1.8 5.2 -1 4 1.6 c-1.2 -2.6 1.6 -3.4 4 -1.6 c4 3 3 9 -4 15 Z" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.6">
        {animated && <animate attributeName="opacity" values="1;0.75;1" dur="1.6s" repeatCount="indefinite" />}
      </path>
      <Spark x={54} y={22} s={4} dur="5.5s" />
    </Emblem>
  );
}

/* Dossier — a classified sealed folder: the danger archive */
export function DossierMark({ size, glow, animated, dimmed }) {
  const u = "sxe-dossier";
  return (
    <Emblem id="dossier" size={size} glow={glow} dimmed={dimmed} ca="#f43f5e" cb="#7f1d1d">
      {/* folder body */}
      <path d="M22 30 h13 l4 5 h19 a3 3 0 0 1 3 3 v18 a3 3 0 0 1 -3 3 H22 a3 3 0 0 1 -3 -3 V33 a3 3 0 0 1 3 -3 Z"
        fill={`url(#${u}-glass)`} stroke={`url(#${u}-ring)`} strokeWidth="1.8" />
      {/* CLASSIFIED bar */}
      <rect x="26" y="44" width="28" height="4" rx="2" fill={`url(#${u}-ring)`} opacity="0.85" />
      <rect x="26" y="52" width="18" height="3" rx="1.5" fill="rgba(255,255,255,0.35)" />
      {/* small lock */}
      <g>
        {animated && <animate attributeName="opacity" values="1;0.6;1" dur="2s" repeatCount="indefinite" />}
        <rect x="52" y="50" width="12" height="9" rx="2" fill="#fecaca" />
        <path d="M54.5 50 v-2.5 a3.5 3.5 0 0 1 7 0 V50" fill="none" stroke="#fecaca" strokeWidth="2" />
        <circle cx="58" cy="54.5" r="1.4" fill="#7f1d1d" />
      </g>
    </Emblem>
  );
}

/* Horror — the installation's hex portal: a sealed gate with a beating red core */
export function HorrorMark({ size, glow, animated, dimmed }) {
  const u = "sxe-horror";
  const hex = (r) =>
    Array.from({ length: 7 }, (_, k) => {
      const a = (k / 6) * Math.PI * 2 - Math.PI / 2;
      return `${40 + Math.cos(a) * r},${40 + Math.sin(a) * r}`;
    }).join(" ");
  return (
    <Emblem id="horror" size={size} glow={glow} dimmed={dimmed} ca="#a855f7" cb="#ff0033">
      {/* facet hex gate */}
      <polygon points={hex(21)} fill={`url(#${u}-glass)`} stroke={`url(#${u}-ring)`} strokeWidth="1.9" />
      <polygon points={hex(14.5)} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.7">
        {animated && (
          <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="26s" repeatCount="indefinite" />
        )}
      </polygon>
      {/* crosshair ticks */}
      <line x1="40" y1="14" x2="40" y2="19" stroke={`url(#${u}-ring)`} strokeWidth="1.2" opacity="0.8" />
      <line x1="40" y1="61" x2="40" y2="66" stroke={`url(#${u}-ring)`} strokeWidth="1.2" opacity="0.8" />
      <line x1="14" y1="40" x2="19" y2="40" stroke={`url(#${u}-ring)`} strokeWidth="1.2" opacity="0.8" />
      <line x1="61" y1="40" x2="66" y2="40" stroke={`url(#${u}-ring)`} strokeWidth="1.2" opacity="0.8" />
      {/* the beating red core + escaping pulse rings */}
      <circle cx="40" cy="40" r="5.2" fill={`url(#${u}-ob)`}>
        {animated && <animate attributeName="r" values="5.2;3.6;5.2" dur="1.9s" repeatCount="indefinite" />}
      </circle>
      <circle cx="40" cy="40" r="9" fill="none" stroke="#ff0033" strokeWidth="0.9">
        {animated && <animate attributeName="r" values="6;16;6" dur="1.9s" repeatCount="indefinite" />}
        {animated && <animate attributeName="opacity" values="0.85;0;0.85" dur="1.9s" repeatCount="indefinite" />}
      </circle>
      <Spark x={53} y={24} s={4} dur="6s" />
    </Emblem>
  );
}

/* ═══════════════════════ NEUE WELTEN (v3 Redesign) ══════════════════════ */

/* Startup — die Blaupausen-Rakete: faceted Rocket auf Ascent-Vektor,
   Blueprint-Bogen darunter, Lime/Cyan-Energie — der Founder-Sandkasten */
export function StartupMark({ size, glow, animated, dimmed }) {
  const u = "sxe-startup";
  return (
    <Emblem id="startup" size={size} glow={glow} dimmed={dimmed} ca="#c6ff4a" cb="#3de8ff">
      {/* blueprint ascent arc + grid ticks */}
      <path d="M20 56 q16 -4 34 -22" fill="none" stroke={`url(#${u}-ring)`} strokeWidth="1.6" strokeDasharray="3 3" opacity="0.75" />
      {[[26, 54], [36, 50], [46, 42]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.1" fill="rgba(255,255,255,0.55)" />
      ))}
      {/* faceted rocket body (diagonal ascent) */}
      <g transform="rotate(38 40 40)">
        <polygon points="40,16 47,34 47,48 33,48 33,34" fill={`url(#${u}-oa)`} stroke="rgba(255,255,255,0.38)" strokeWidth="0.7" strokeLinejoin="round" />
        <polygon points="40,16 47,34 40,38 33,34" fill="#ffffff" opacity="0.16" />
        {/* window — glass orb */}
        <circle cx="40" cy="36" r="3.6" fill={`url(#${u}-ob)`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />
        {/* fins */}
        <polygon points="33,44 26,54 33,50" fill={`url(#${u}-ob)`} opacity="0.9" />
        <polygon points="47,44 54,54 47,50" fill={`url(#${u}-ob)`} opacity="0.9" />
        {/* thrust flame — breathing */}
        <polygon points="36,49 44,49 40,60" fill="#ffd166">
          {animated && <animate attributeName="opacity" values="1;0.45;1" dur="0.9s" repeatCount="indefinite" />}
        </polygon>
      </g>
      <Spark x={57} y={22} s={5} dur="6s" />
    </Emblem>
  );
}

/* Future City — die Skyline von morgen: drei volumetrische Türme mit
   Licht-Fenstern, darüber ein orbitender Zukunfts-Orb */
export function FutureCityMark({ size, glow, animated, dimmed }) {
  const u = "sxe-fcity";
  return (
    <Emblem id="fcity" size={size} glow={glow} dimmed={dimmed} ca="#22d3ee" cb="#39ff14">
      {/* ground line */}
      <path d="M20 56 h40" stroke="rgba(255,255,255,0.35)" strokeWidth="1" strokeLinecap="round" />
      {/* three towers: front face + lighter top facet */}
      {[{ x: 24, y: 34, w: 9 }, { x: 36, y: 24, w: 10 }, { x: 49, y: 40, w: 8 }].map((t, i) => (
        <g key={i}>
          <rect x={t.x} y={t.y} width={t.w} height={56 - t.y} rx="1.4" fill={`url(#${u}-oa)`} opacity={0.8 + i * 0.07} />
          <polygon points={`${t.x},${t.y} ${t.x + t.w},${t.y} ${t.x + t.w + 2.6},${t.y - 2.6} ${t.x + 2.6},${t.y - 2.6}`} fill="#ffffff" opacity="0.32" />
          <rect x={t.x} y={t.y} width={t.w} height={56 - t.y} rx="1.4" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />
          {/* window lights */}
          {[0, 1, 2].map((r) => (
            <rect key={r} x={t.x + 2} y={t.y + 4 + r * 6} width={t.w - 4} height="2" rx="0.8" fill="#eaffea" opacity="0.75">
              {animated && <animate attributeName="opacity" values={r % 2 ? "0.75;0.25;0.75" : "0.3;0.8;0.3"} dur={`${2.2 + r * 0.7}s`} repeatCount="indefinite" />}
            </rect>
          ))}
        </g>
      ))}
      {/* the future-orb rising over the skyline */}
      <circle cx="58" cy="24" r="4.4" fill={`url(#${u}-ob)`} stroke="rgba(255,255,255,0.45)" strokeWidth="0.6">
        {animated && <animate attributeName="cy" values="24;21.5;24" dur="3.4s" repeatCount="indefinite" />}
      </circle>
      <Spark x={24} y={24} s={4} dur="7s" />
    </Emblem>
  );
}

/* RapVid — das spielbare Musikvideo: faceted Play-Kristall im Filmrahmen,
   lebender Equalizer darunter — Magenta/Violett-Energie */
export function RapVidMark({ size, glow, animated, dimmed }) {
  const u = "sxe-rapvid";
  return (
    <Emblem id="rapvid" size={size} glow={glow} dimmed={dimmed} ca="#ff3df0" cb="#7b5cff">
      {/* film frame with sprocket notches */}
      <rect x="24" y="22" width="32" height="24" rx="3.4" fill={`url(#${u}-glass)`} stroke={`url(#${u}-ring)`} strokeWidth="1.6" />
      {[28, 36, 44, 52].map((x) => (
        <rect key={x} x={x - 1.2} y="19.4" width="2.4" height="2.2" rx="0.7" fill="rgba(255,255,255,0.4)" />
      ))}
      {/* faceted play crystal */}
      <polygon points="36,27 48,34 36,41" fill={`url(#${u}-ob)`} stroke="rgba(255,255,255,0.45)" strokeWidth="0.7" strokeLinejoin="round" />
      <polygon points="36,27 48,34 36,34" fill="#ffffff" opacity="0.18" />
      {/* living equalizer */}
      {[[28, 7], [33, 11], [38, 6], [43, 12], [48, 8]].map(([x, h], i) => (
        <rect key={i} x={x} y={58 - h} width="3" height={h} rx="1.4" fill={`url(#${u}-oa)`}>
          {animated && <animate attributeName="height" values={`${h};${h * 0.45};${h}`} dur={`${0.8 + i * 0.13}s`} repeatCount="indefinite" />}
          {animated && <animate attributeName="y" values={`${58 - h};${58 - h * 0.45};${58 - h}`} dur={`${0.8 + i * 0.13}s`} repeatCount="indefinite" />}
        </rect>
      ))}
      <Spark x={55} y={25} s={4} dur="5.5s" />
    </Emblem>
  );
}
